/**
 * This component wraps regular VODplayer with receiver from Player.js lib
 * in order to listen and react on POST messages from the parent iframe
 * read more: https://github.com/embedly/player.js#receiver
 */
import React from 'react';
import PropTypes from 'prop-types';
import playerjs from 'player.js';
import { VODPlayer } from '@wix/wix-vod-shared/vod-player';

import { CONTROL_DESCRIPTOR } from '@wix/wix-vod-annoto';
import {
  VIDEO_EVENTS as PLAYER_VIDEO_EVENTS,
  ENGINE_STATES,
  UI_EVENTS,
} from 'playable/dist/src';

const PLAYER_JS_STATES = {
  PLAY: 'play',
  ENDED: 'ended',
  TIME_UPDATED: 'timeupdate',

  PAUSE: 'pause',
  GET_PAUSED: 'getPaused',

  MUTE: 'mute',
  UN_MUTE: 'unmute',
  GET_MUTED: 'getMuted',

  SET_VOLUME: 'setVolume',
  GET_VOLUME: 'getVolume',

  GET_DURATION: 'getDuration',

  SET_CURRENT_TIME: 'setCurrentTime',
  GET_CURRENT_TIME: 'getCurrentTime',

  SET_LOOP: 'setLoop',
  GET_LOOP: 'getLoop',

  annoto: {
    events: {
      ON_READY: 'onReady',
      ON_SEEK: 'onSeek',
      ON_CONTROLS_ENTER: 'onControlsEnter',
      ON_CONTROLS_LEAVE: 'onControlsLeave',
    },
    methods: {
      IS_LIVE: 'isLive',
      MEDIA_SRC: 'mediaSrc',
      AUTOPLAY: 'autoplay',
      CONTROLS_HEIGHT: 'controlsHeight',
      TRACK_MARGIN_LEFT: 'trackMarginLeft',
      TRACK_MARGIN_RIGHT: 'trackMarginRight',
    },
  },
};

const wrapWithReceiver = (VODlayer) => {
  class VodPlayerWithReceiver extends React.Component {
    static propTypes = {
      onPlayRequested: PropTypes.func,
      onInit: PropTypes.func,
      vidiUrls: PropTypes.array,
    };

    static defaultProps = {
      onInit: () => {},
    };

    componentDidUpdate() {
      this.tryToSendReceiverReadyNotification();
    }

    tryToSendReceiverReadyNotification = () => {
      if (this.receiver && !this.receiver.isReady && this.props.vidiUrls) {
        this.receiver.ready();
        this.receiver.send(PLAYER_JS_STATES.annoto.events.ON_READY);
      }
    };

    onInit = (player, ...args) => {
      this.player = player;
      this.receiver = new playerjs.Receiver();

      // we extend supported methods of playerjs
      Object.assign(playerjs.METHODS, PLAYER_JS_STATES.annoto.methods);

      this.setListenersForReceiver();
      this.setListenersForPlayer();
      this.props.onInit(player, ...args);

      this.tryToSendReceiverReadyNotification();
    };

    setListenersForPlayer = () => {
      const player = this.player;
      const receiver = this.receiver;

      player.on(PLAYER_VIDEO_EVENTS.PLAY_ABORTED, () => {
        /*
          in a lot of cases browser allow to play unmuted video just after direct user interaction,
          in such case, player will fire PLAY_ABORTED event, after which we will mute video and redo play command
        */
        player.setMute(true);
        setTimeout(() => player.play());
      });

      player.on(UI_EVENTS.MAIN_BLOCK_SHOW, () =>
        receiver.send(PLAYER_JS_STATES.annoto.events.ON_CONTROLS_ENTER),
      );
      player.on(UI_EVENTS.MAIN_BLOCK_HIDE, () =>
        receiver.send(PLAYER_JS_STATES.annoto.events.ON_CONTROLS_LEAVE),
      );

      player.on(ENGINE_STATES.PLAYING, () =>
        receiver.emit(PLAYER_JS_STATES.PLAY),
      );

      player.on(ENGINE_STATES.PAUSED, () =>
        receiver.emit(PLAYER_JS_STATES.PAUSE),
      );

      player.on(ENGINE_STATES.ENDED, () =>
        receiver.emit(PLAYER_JS_STATES.ENDED),
      );

      player.on(PLAYER_VIDEO_EVENTS.CURRENT_TIME_UPDATED, (currentTime) => {
        receiver.emit(PLAYER_JS_STATES.TIME_UPDATED, {
          seconds: currentTime,
          duration: player.getDuration(),
        });
      });

      player.on(PLAYER_VIDEO_EVENTS.SEEK_IN_PROGRESS, () => {
        receiver.send(PLAYER_JS_STATES.annoto.events.ON_SEEK);
      });
    };

    setListenersForReceiver = () => {
      const receiver = this.receiver;
      const player = this.player;

      receiver.on(PLAYER_JS_STATES.annoto.methods.IS_LIVE, () => {
        // TODO in latest versions of playable
        return receiver.send(
          PLAYER_JS_STATES.annoto.methods.IS_LIVE,
          player._defaultModules.isDynamicContent,
        );
      });

      receiver.on(PLAYER_JS_STATES.annoto.methods.MEDIA_SRC, () => {
        return receiver.send(
          PLAYER_JS_STATES.annoto.methods.MEDIA_SRC,
          player.getSrc(),
        );
      });

      receiver.on(PLAYER_JS_STATES.annoto.methods.AUTOPLAY, () => {
        return receiver.send(
          PLAYER_JS_STATES.annoto.methods.AUTOPLAY,
          player.getAutoplay(),
        );
      });

      receiver.on(PLAYER_JS_STATES.annoto.methods.AUTOPLAY, () => {
        return receiver.send(
          PLAYER_JS_STATES.annoto.methods.AUTOPLAY,
          player.getAutoplay(),
        );
      });

      receiver.on(PLAYER_JS_STATES.annoto.methods.CONTROLS_HEIGHT, () => {
        return receiver.send(
          PLAYER_JS_STATES.annoto.methods.CONTROLS_HEIGHT,
          CONTROL_DESCRIPTOR.height,
        );
      });

      receiver.on(PLAYER_JS_STATES.annoto.methods.TRACK_MARGIN_LEFT, () => {
        return receiver.send(
          PLAYER_JS_STATES.annoto.methods.TRACK_MARGIN_LEFT,
          CONTROL_DESCRIPTOR.track.marginLeft,
        );
      });

      receiver.on(PLAYER_JS_STATES.annoto.methods.TRACK_MARGIN_RIGHT, () => {
        return receiver.send(
          PLAYER_JS_STATES.annoto.methods.TRACK_MARGIN_RIGHT,
          CONTROL_DESCRIPTOR.track.marginLeft,
        );
      });

      receiver.on(PLAYER_JS_STATES.PLAY, () => player.play());

      receiver.on(PLAYER_JS_STATES.PAUSE, () => player.pause());

      receiver.on(PLAYER_JS_STATES.GET_PAUSED, (callback) =>
        callback(player.isPaused),
      );

      receiver.on(PLAYER_JS_STATES.MUTE, () => player.mute());

      receiver.on(PLAYER_JS_STATES.UN_MUTE, () => player.unmute());

      receiver.on(PLAYER_JS_STATES.GET_MUTED, (callback) =>
        callback(player.getMute()),
      );

      receiver.on(PLAYER_JS_STATES.SET_VOLUME, (value) =>
        player.setVolume(value),
      );

      receiver.on(PLAYER_JS_STATES.GET_VOLUME, (callback) =>
        callback(player.getVolume()),
      );

      receiver.on(PLAYER_JS_STATES.GET_DURATION, (callback) =>
        callback(player.getDuration()),
      );

      receiver.on(PLAYER_JS_STATES.SET_CURRENT_TIME, (value) =>
        player.seekTo(value),
      );

      receiver.on(PLAYER_JS_STATES.GET_CURRENT_TIME, (callback) =>
        callback(player.getCurrentTime()),
      );

      receiver.on(PLAYER_JS_STATES.SET_LOOP, (value) => player.setLoop(value));

      receiver.on(PLAYER_JS_STATES.GET_LOOP, (callback) =>
        callback(player.getLoop()),
      );
    };

    render() {
      return <VODPlayer {...this.props} onInit={this.onInit} />;
    }
  }

  return VodPlayerWithReceiver;
};

export default wrapWithReceiver;

export const VodPlayerWithReceiver = wrapWithReceiver(VODPlayer);
